<script setup>

const props = defineProps({
  leadGenFields: Object,
  pageSettings: Object
})
const { getBrochureRequestFormFields, sendLeadGenRequest, sanitizeLeadGenPayload, processLeadSubmission, validateWithZeroBounce } = useLeadGenData();
const learningStylesLabels = props.leadGenFields.learningStylesCheckboxes
const formData = ref(getBrochureRequestFormFields());
const ages = Array(13).fill().map((element, i) => {
  return { 
    value: parseInt(new Date().getFullYear()) - (i + 7), 
    label: i + 7 
  };
});
const brochureRequestStore = useBrochureRequest();
const config = useRuntimeConfig()

const firstName = ref('');
const postalCode = ref('');
const downloadBrochure = ref();
const emailField = ref(null);
const inputPhoneNumber = ref('');

const onFieldChanged = (field) => {
  for (const key in formData.value) {
    // Saving all the fields data while they are being updated    
    if (field.id === key) {
      formData.value[key].value = field.value;
      formData.value[key].valid = field.valid;
      // Getting First name and zip code values
      if (key === 'firstName') {
        firstName.value = field.value;
      }
      else if (key === 'postalCode') {
        postalCode.value = field.value;
      }
    }
    // Saving checkboxes data
    else if (field.name && field.name === key) {
      if (field.checked) {
        formData.value[key].value.push(field.value);
      }
      else {
        const index = formData.value[key].value.indexOf(field.value);
        if (index !== -1) {
          formData.value[key].value.splice(index, 1);
        }
      }     
    }
  } 
}

const btnSubmitClass = computed(() => {
  return (!formIsValid.value || requestSent.value ? 'disabled' : '')
})

const formIsValid = computed(() => {  
  for (const key in formData.value) {
    if (!formData.value[key].valid) {
      return false;
    }
  }

  return true;
})

const submitButtonText = computed(() => {
  return requestSent.value ? 'Sending...' : 'Submit';
})

const requestSent = ref(false)
const handleSubmit = async (event) => {
  requestSent.value = true;
  const payload = sanitizeLeadGenPayload(formData.value);
  const hashedData = hashObject(payload); 

  dataLayer.push({
    'event': 'formSubmitted',
    'leadsUserData': hashedData
  });

  if (props.pageSettings.zeroBounceValidation) {
    const encodedEmail = encodeURIComponent(payload.emailAddress)
    const { data, error } = await validateWithZeroBounce(encodedEmail)
    

    if (data.value.status === 'invalid' ) {
      emailField.value.handleState('invalid')
    }
    else {
      await submitForm(payload);
    } 
  }   
  else {
    await submitForm(payload);
  }

  requestSent.value = false;
  
}

const submitForm = async (payload) => {    
  // Process the lead submission
  const apiResponse = await processLeadSubmission(payload)  

  if (apiResponse) {
    const redirectUrl = payload.sendPrintedInformation
      ? '/brochurerequest-thankyou-printed'
      : '/brochurerequest-thankyou-digital'

    window.location.href = redirectUrl
  } 

}


onMounted(() => {
   // Resetting the form in case they back up
  let form = document.getElementById('br-form')
  form.reset()
  const fieldsets = form.getElementsByTagName('fieldset');
  fieldsets[0].removeAttribute('disabled')

  const referrer = document.referrer;
  if (referrer.includes('brochurerequest-thankyou-digital') && brochureRequestStore.firstName !== '') {
    document.getElementById('firstName').value = brochureRequestStore.firstName;
    onFieldChanged({id: 'firstName', value: brochureRequestStore.firstName, valid: true})
    document.getElementById('emailAddress').value = brochureRequestStore.email;
    onFieldChanged({id: 'emailAddress', value: brochureRequestStore.email, valid: true})
    document.getElementById('postalCode').value = brochureRequestStore.zipCode;
    onFieldChanged({id: 'postalCode', value: brochureRequestStore.zipCode, valid: true})
    // Age update
    const ageDropdown = document.getElementById('studentBirthYears');
    ageDropdown.value = brochureRequestStore.childAges;
    // Mark as checked age checkboxes    
    brochureRequestStore.childAges.forEach((e) => {
      const chbId = e - 7;      
      document.getElementById(`chb-${chbId}`).checked = true;
    }) 
    const labels = document.getElementsByClassName('dropdown-content')
    labels[0].innerHTML = brochureRequestStore.childAges
    const fl = document.getElementsByClassName('floating-label active no-value');
    fl[0].classList.remove('no-value')
    document.getElementById('brochurePreferencePrinted').click(() => {
      // Updating mail brochure form fields 
      document.getElementById('brochureFirstName').value = brochureRequestStore.firstName;
      document.getElementById('brochurePostalCode').value = brochureRequestStore.zipCode;
    })

    downloadBrochure.value.onFieldChanged({id: 'brochureFirstName', value: brochureRequestStore.firstName, valid: true})
    downloadBrochure.value.onFieldChanged({id: 'brochurePostalCode', value: brochureRequestStore.zipCode, valid: true})

    const download = {
      brochureFirstName: {
        value: brochureRequestStore.firstName,
        required: true, 
        valid: true
      },
      brochurePostalCode: {
        value: brochureRequestStore.zipCode,
        required: true, 
        valid: true
      },
    }
    // onFieldChanged({id: 'brochure', value: download, valid: false})

    //Checking interests values
    brochureRequestStore.interests.forEach((e) => {
      if (e == 405000) {
        document.getElementById('chbOnCampus').checked = true;
      }
      else {
        document.getElementById('chbOnline').checked = true;
      }
    })

    
  }
});

</script>

<template>
  <div class=" mt-4" id="brochure-request-form" data-load="eager">
    <div class="row justify-content-center form-wrapper mb-8 p-3 p-md-5 ">      
      <div class="col-12 col-lg-6">
        <p class="fst-italic col">* Indicates required field</p>
        <div class="col">
          <UIHeading
            :subtitleComponent="pageSettings.titlePersonalInformation" 
            :subtitleClass="'title '" /> 
        </div>              
        <form id="br-form" class="needs-validation" ref="formFields" @submit.prevent="handleSubmit" novalidate>
          <fieldset disabled="disabled">
            <div class="col mb-2">
              <FormFloatingLabelInput :id="'firstName'" :placeholderText="leadGenFields.namePlaceholderText" :errorText="leadGenFields.nameErrorMessage" :required="true" @onChanged="onFieldChanged"  />
            </div>
            <div class="col mb-2">
              <FormFloatingLabelInput :id="'emailAddress'" :type="'email'" :placeholderText="leadGenFields.emailFields.emailPlaceholderText" :errorText="leadGenFields.emailFields.emailErrorMessage" :invalidText="leadGenFields.emailFields.invalidEmailErrorMessage" :required="true" @onChanged="onFieldChanged" ref="emailField"  />
            </div>
            <div class="col mb-2" v-if="pageSettings.showPhoneNumber">
              <FormFloatingLabelInput :id="'phoneNumber'" :type="'tel'" :placeholderText="leadGenFields.phonePlaceholderText" :errorText="leadGenFields.phoneErrorText" :required="false" @onChanged="onFieldChanged" />
            </div>
            <div class="col mb-2">
              <FormFloatingLabelInput :id="'postalCode'" :placeholderText="leadGenFields.zipcodePlaceholderText" :errorText="leadGenFields.zipcodeErrorMessage" @onChanged="onFieldChanged" />
            </div>
            <div class="col multi-select">
              <UIMultiSelectDropdown :inputName="'studentBirthYears'" :options="ages" :multiSelectLabel="leadGenFields.studentAgeTitle" @onChanged="onFieldChanged" />
            </div>          
            <div class="form-heading checkbox-heading col">
              <div class="checkbox-title">{{ leadGenFields.titleProgramType }}</div>
              <div class="checkbox-subtitle">{{ leadGenFields.subTitleProgramType }}</div>
            </div>
            
            
            <div class="checkbox-group">
              <div class="col" v-if="!!learningStylesLabels.OnCampusSummerProgramsLabel">              
                <FormFieldCheckbox :id="'chbOnCampus'"  :name="'learningStyleTypeIDs'" :value="405000" :label="learningStylesLabels.OnCampusSummerProgramsLabel" @onChanged="onFieldChanged" />
              </div>
              <div class="col" v-if="!!learningStylesLabels.OnlineCampsClassesLabel">              
                <FormFieldCheckbox :id="'chbOnline'" :name="'learningStyleTypeIDs'" :value="405001" :label="learningStylesLabels.OnlineCampsClassesLabel" @onChanged="onFieldChanged" />
              </div>
              <div class="col" v-if="!!learningStylesLabels.SmallGroupOnlineLabel">              
                <FormFieldCheckbox :id="'chbSmallGroup'" :name="'learningStyleTypeIDs'" :value="405002" :label="learningStylesLabels.SmallGroupOnlineLabel" @onChanged="onFieldChanged" />
              </div>
              <div class="col" v-if="!!learningStylesLabels.OneOnOnePrivateLessonsLabel">              
                <FormFieldCheckbox :id="'chbOneOnOne'" :name="'learningStyleTypeIDs'" :value="405003" :label="learningStylesLabels.OneOnOnePrivateLessonsLabel" @onChanged="onFieldChanged" />
              </div>
            </div>          
            <FormFieldDownloadBrochure @onChanged="onFieldChanged" :leadGenFields="leadGenFields" :pageSettings="pageSettings" v-if="pageSettings.printedBrochure" :firstName="firstName" :postalCode="postalCode" ref="downloadBrochure" />
            <div class="col mt-3">
              <button class="btn display btn-primary" :class="btnSubmitClass"  type="submit">{{ submitButtonText }}</button>
            </div>
            <div class="form-disclaimer" v-if="pageSettings.showPhoneNumber">
              By submitting your number, you consent to receive occasional text messages from iD Tech for the purpose of providing more information about our services. Texting and data rates may apply.
            </div>
            <div class="form-disclaimer">
              By signing up you agree to our <a href="#privacy-policy" data-bs-toggle="modal" data-bs-target="#privacy-policy-footer">Privacy Policy</a>
            </div>  
          </fieldset>
        </form>
      </div>  
    </div>
  </div>
</template>

<style lang="scss">
#brochure-request-form {
  form fieldset[disabled^="disabled"] {
    .multi-select {
      .form-input.fake-input.form-select {
        background-color: #e9ecef !important;
      }
    }
  }
}

</style>
<style lang="scss" scoped>
#brochure-request-form {
  form fieldset[disabled^="disabled"] {
    .multi-select {
      .form-input.fake-input.form-select {
        background-color: #e9ecef !important;
      }
    }
  }
  .fst-italic {
    font-size: 12px;
  }
  .form-wrapper {
    background-color: $color-accent;
    @include media-breakpoint-up(md) {
      border-radius: 12px;
    }
    .multi-select {
      .input-container {
        margin-top: 0px;
        background-color: $color-secondary;
        
      }
    }
    
  }
}
</style>